import { useEffect } from 'react';
import {
    isRouteErrorResponse,
    useRouteError,
    useLocation,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import Block from '@shared/components/ui/Block';
import { Text } from '@/components/Text';

/**
 * Route-level error element. Replaces the old `_boundary.tsx` `ErrorBoundary`
 * export. Use as `errorElement` on the boundary layout route.
 */
export function RouteError() {
    const error = useRouteError();
    const location = useLocation();

    // Session valid but backend user can't be fetched — force re-auth.
    const isUserNotFound =
        error instanceof Error && error.message.includes('User not found');

    useEffect(() => {
        if (isUserNotFound) window.location.href = '/login';
    }, [isUserNotFound]);

    useEffect(() => {
        if (isUserNotFound) return;
        const routeContext = { path: location.pathname };
        if (isRouteErrorResponse(error)) {
            const level =
                error.status >= 500
                    ? 'error'
                    : error.status >= 400
                    ? 'warning'
                    : 'info';
            Sentry.withScope((scope) => {
                scope.setLevel(level as Sentry.SeverityLevel);
                scope.setTag('http.status', String(error.status));
                scope.setContext('route', routeContext);
                Sentry.captureMessage(
                    `${error.status} ${error.statusText} at ${location.pathname}`
                );
            });
        } else {
            Sentry.withScope((scope) => {
                scope.setLevel('error');
                scope.setContext('route', routeContext);
                Sentry.captureException(error);
            });
        }
    }, [error, isUserNotFound, location.pathname]);

    if (isUserNotFound) return null;

    return (
        <Block $verticalAlign="center" $align="center" style={{ height: '100vh' }}>
            <ErrorPageWrapper>
                <Text as="h1" size="xxLarge" weight="bold" align="center" spacing="xLarge">
                    Something went wrong!
                </Text>
                <Text size="large" align="center">
                    We've been notified that this happened and will get it sorted.
                    In the meantime try refreshing the browser.
                </Text>
            </ErrorPageWrapper>
        </Block>
    );
}

const ErrorPageWrapper = styled.div`
    padding: 40px;
    border-radius: 16px;
    background-color: ${({ theme }) => theme.colors.white};
    max-width: 570px;
`;
